import { useEffect, useMemo, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Button,
  Checkbox,
  Container,
  Heading,
  Input,
  Label,
  RadioGroup,
  StatusBadge,
  Text,
  clx,
  toast,
} from "@medusajs/ui"

import { SingleColumnPage } from "../../components/layout/pages"
import {
  useCatalogTranslationSettings,
  useRefreshCatalogTranslationHealth,
  useTestCatalogTranslationConnection,
  useUpdateCatalogTranslationSettings,
  type CatalogTranslationFieldScopes,
  type CatalogTranslationProvider,
  type CatalogTranslationSettings,
  type CatalogTranslationLocale,
  type UpdateCatalogTranslationSettingsInput,
} from "../../hooks/api/catalog-translation-settings"
import { getAdminLanguageDisplayName } from "../../i18n/supported-admin-languages"

const PROVIDER_FIELDS: Record<Exclude<CatalogTranslationProvider, "none">, string[]> = {
  deepl: ["api_key"],
  google: ["api_key"],
}

const localeOptions: CatalogTranslationLocale[] = ["en", "vi"]

const EMPTY_FIELD_SCOPES: CatalogTranslationFieldScopes = {
  product_title: true,
  product_subtitle: true,
  product_description: true,
  category_name: true,
}

const PROVIDER_OPTIONS = ["none", "deepl", "google"] as const

const AUTOMATION_OPTIONS: Array<
  [keyof CatalogTranslationSettings, "autoTranslateOnCreate" | "autoTranslateOnUpdate" | "preserveManualEdits"]
> = [
  ["auto_translate_on_create", "autoTranslateOnCreate"],
  ["auto_translate_on_update", "autoTranslateOnUpdate"],
  ["preserve_manual_edits", "preserveManualEdits"],
]

const FIELD_SCOPE_OPTIONS: Array<
  [keyof CatalogTranslationFieldScopes, "productTitle" | "productSubtitle" | "productDescription" | "categoryName"]
> = [
  ["product_title", "productTitle"],
  ["product_subtitle", "productSubtitle"],
  ["product_description", "productDescription"],
  ["category_name", "categoryName"],
]

const OPTION_CARD_CLASS =
  "group flex items-center gap-3 rounded-lg border border-ui-border-base px-4 py-3 transition-colors focus-within:shadow-borders-focus"

const getOptionCardClassName = (selected: boolean, disabled = false) =>
  clx(OPTION_CARD_CLASS, {
    "border-ui-border-interactive bg-ui-bg-base": selected,
    "hover:border-ui-border-strong": !disabled,
    "opacity-50": disabled,
  })

function confirmDestructiveAction(message: string): boolean {
  if (typeof window === "undefined") {
    return true
  }

  return window.confirm(message)
}

export function hasStoredCredential(
  settings: CatalogTranslationSettings | null,
  field: string
) {
  const value = settings?.credentials?.[field]
  return typeof value === "string" && value.trim().length > 0
}

export function buildDraftWarningCodes(input: {
  settings: CatalogTranslationSettings | null
  credentials: Record<string, string>
  clearedCredentialKeys: string[]
  requiredFields: string[]
}) {
  if (!input.settings) {
    return []
  }

  const warnings: string[] = []
  const hasAutoFlag =
    input.settings.auto_translate_on_create ||
    input.settings.auto_translate_on_update

  const providerCredentialsReady =
    input.settings.active_provider !== "none" &&
    input.requiredFields.every((field) => {
      if (input.clearedCredentialKeys.includes(field)) {
        return false
      }

      const draftValue = input.credentials[field]
      if (typeof draftValue === "string" && draftValue.trim().length > 0) {
        return true
      }

      return hasStoredCredential(input.settings, field)
    })

  if (
    hasAutoFlag &&
    (input.settings.active_provider === "none" || !providerCredentialsReady)
  ) {
    warnings.push("provider_none_or_credentials_not_ready")
  }

  const scopes = input.settings.field_scopes
  const allScopesOff =
    scopes.product_title === false &&
    scopes.product_subtitle === false &&
    scopes.product_description === false &&
    scopes.category_name === false

  if (allScopesOff) {
    warnings.push("all_scopes_off_governance")
  }

  return warnings
}

export type CatalogTranslationAlertCode =
  | "page_error"
  | "provider_none_or_credentials_not_ready"
  | "all_scopes_off_governance"

export type CatalogTranslationPrimaryCta = "retry" | "provider" | "scope"

export function resolveCatalogTranslationAlerts(input: {
  pageError: string | null
  warningCodes: string[]
}): Array<{
  code: CatalogTranslationAlertCode
  primaryCta: CatalogTranslationPrimaryCta | null
}> {
  const alerts: CatalogTranslationAlertCode[] = []

  if (input.pageError) {
    alerts.push("page_error")
  }

  if (input.warningCodes.includes("provider_none_or_credentials_not_ready")) {
    alerts.push("provider_none_or_credentials_not_ready")
  }

  if (input.warningCodes.includes("all_scopes_off_governance")) {
    alerts.push("all_scopes_off_governance")
  }

  return alerts.map((code, index) => ({
    code,
    primaryCta:
      index !== 0
        ? null
        : code === "page_error"
          ? "retry"
          : code === "provider_none_or_credentials_not_ready"
            ? "provider"
            : "scope",
  }))
}

export function resolveTestConnectionSectionError(errorMessage: string | null) {
  if (!errorMessage) {
    return null
  }

  return {
    titleKey: "catalogTranslationSettings.errors.testConnectionFailedTitle",
    message: errorMessage,
  }
}

export function resolveFirstUnresolvedCredentialField(input: {
  settings: CatalogTranslationSettings | null
  requiredFields: string[]
  credentials: Record<string, string>
  clearedCredentialKeys: string[]
}): string | null {
  if (!input.settings || input.settings.active_provider === "none") {
    return null
  }

  for (const field of input.requiredFields) {
    if (input.clearedCredentialKeys.includes(field)) {
      return field
    }

    const draftValue = input.credentials[field]
    if (typeof draftValue === "string" && draftValue.trim().length > 0) {
      continue
    }

    if (!hasStoredCredential(input.settings, field)) {
      return field
    }
  }

  return null
}

export function buildCatalogTranslationSettingsUpdatePayload(input: {
  settings: CatalogTranslationSettings
  credentials?: Record<string, string | null>
}): UpdateCatalogTranslationSettingsInput {
  const payload: UpdateCatalogTranslationSettingsInput = {
    active_provider: input.settings.active_provider,
    enabled_locales: input.settings.enabled_locales,
    auto_translate_on_create: input.settings.auto_translate_on_create,
    auto_translate_on_update: input.settings.auto_translate_on_update,
    preserve_manual_edits: input.settings.preserve_manual_edits,
    field_scopes: input.settings.field_scopes,
    retry_policy: input.settings.retry_policy,
  }

  if (input.credentials) {
    payload.credentials = input.credentials
  }

  return payload
}

export const CatalogTranslationSettingsPage = () => {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useCatalogTranslationSettings()
  const updateMutation = useUpdateCatalogTranslationSettings()
  const testMutation = useTestCatalogTranslationConnection()
  const refreshHealthMutation = useRefreshCatalogTranslationHealth()

  const [settings, setSettings] = useState<CatalogTranslationSettings | null>(null)
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [dirtyCredentialKeys, setDirtyCredentialKeys] = useState<string[]>([])
  const [clearedCredentialKeys, setClearedCredentialKeys] = useState<string[]>([])
  const [pageError, setPageError] = useState<string | null>(null)
  const [testConnectionError, setTestConnectionError] = useState<string | null>(null)
  const providerSectionRef = useRef<HTMLElement | null>(null)
  const credentialInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const fieldScopesSectionRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!data?.catalog_translation_settings) {
      return
    }

    const incoming = data.catalog_translation_settings
    setSettings(incoming)
    setCredentials((incoming.credentials as Record<string, string> | null) ?? {})
    setDirtyCredentialKeys([])
    setClearedCredentialKeys([])
    setPageError(null)
    setTestConnectionError(null)
  }, [data])

  const requiredFields = useMemo(() => {
    if (!settings || settings.active_provider === "none") {
      return []
    }

    return PROVIDER_FIELDS[settings.active_provider]
  }, [settings])

  const draftWarningCodes = useMemo(
    () =>
      buildDraftWarningCodes({
        settings,
        credentials,
        clearedCredentialKeys,
        requiredFields,
      }),
    [settings, credentials, clearedCredentialKeys, requiredFields]
  )
  const alerts = useMemo(
    () =>
      resolveCatalogTranslationAlerts({
        pageError,
        warningCodes: draftWarningCodes,
      }),
    [pageError, draftWarningCodes]
  )
  const testConnectionSectionError = useMemo(
    () => resolveTestConnectionSectionError(testConnectionError),
    [testConnectionError]
  )

  const providerHealthStatus = settings?.provider_health_summary?.status ?? "idle"
  const healthColor =
    providerHealthStatus === "healthy"
      ? "green"
      : providerHealthStatus === "attention"
        ? "red"
        : "grey"

  const toggleLocale = (locale: "en" | "vi", checked: boolean) => {
    if (!settings) {
      return
    }

    const current = new Set(settings.enabled_locales)
    if (checked) {
      current.add(locale)
    } else if (current.size > 1) {
      current.delete(locale)
    }

    setSettings({
      ...settings,
      enabled_locales: Array.from(current) as CatalogTranslationLocale[],
    })
  }

  const updateFieldScope = (key: keyof CatalogTranslationFieldScopes, checked: boolean) => {
    if (!settings) {
      return
    }

    setSettings({
      ...settings,
      field_scopes: {
        ...(settings.field_scopes ?? EMPTY_FIELD_SCOPES),
        [key]: checked,
      },
    })
  }

  const handleSave = async () => {
    if (!settings) {
      return
    }

    const nextCredentials =
      settings.active_provider === "none"
        ? undefined
        : Object.fromEntries(
            requiredFields.flatMap((field) => {
              if (clearedCredentialKeys.includes(field)) {
                return [[field, null] as const]
              }

              if (!dirtyCredentialKeys.includes(field)) {
                return []
              }

              const normalized = String(credentials[field] || "").trim()
              return normalized ? [[field, normalized] as const] : []
            })
          )

    try {
      setPageError(null)
      const response = await updateMutation.mutateAsync(
        buildCatalogTranslationSettingsUpdatePayload({
          settings,
          credentials: nextCredentials,
        })
      )
      setSettings(response.catalog_translation_settings)
      setCredentials(
        (response.catalog_translation_settings.credentials as Record<string, string> | null) ?? {}
      )
      setDirtyCredentialKeys([])
      setClearedCredentialKeys([])
      toast.success(t("catalogTranslationSettings.toasts.saveSuccess"))
    } catch (error: any) {
      setPageError(error?.message || t("catalogTranslationSettings.errors.saveFailed"))
      toast.error(
        error?.message || t("catalogTranslationSettings.toasts.saveError")
      )
    }
  }

  const handleTestConnection = async () => {
    if (!settings || settings.active_provider === "none") {
      toast.error(t("catalogTranslationSettings.toasts.selectProviderBeforeTest"))
      return
    }

    const nextCredentials = Object.fromEntries(
      Object.entries(credentials).filter(([, value]) => String(value || "").trim())
    )

    if (
      requiredFields.some(
        (field) =>
          (!nextCredentials[field] && !hasStoredCredential(settings, field)) ||
          clearedCredentialKeys.includes(field)
      )
    ) {
      toast.error(t("catalogTranslationSettings.toasts.fillCredentialsBeforeTest"))
      return
    }

    try {
      setTestConnectionError(null)
      const response = await testMutation.mutateAsync({
        provider: settings.active_provider,
        credentials: nextCredentials,
      })

      if (response.test_connection.ok) {
        setTestConnectionError(null)
        toast.success(
          response.test_connection.message ||
            t("catalogTranslationSettings.toasts.testSuccess")
        )
      } else {
        const sectionErrorMessage =
          response.test_connection.message ||
          t("catalogTranslationSettings.toasts.testFailed")
        setTestConnectionError(sectionErrorMessage)
        toast.error(
          sectionErrorMessage
        )
      }

      if (response.provider_health_summary) {
        setSettings((current) =>
          current
            ? {
                ...current,
                provider_health_summary: response.provider_health_summary!,
              }
            : current
        )
      }
    } catch (error: any) {
      const sectionErrorMessage =
        error?.message || t("catalogTranslationSettings.toasts.testError")
      setTestConnectionError(sectionErrorMessage)
      toast.error(
        sectionErrorMessage
      )
    }
  }

  const handleRefreshHealth = async () => {
    try {
      const response = await refreshHealthMutation.mutateAsync()
      setSettings((current) =>
        current
          ? {
              ...current,
              provider_health_summary: response.catalog_translation_health,
            }
          : current
      )
      toast.success(t("catalogTranslationSettings.toasts.healthRefreshSuccess"))
    } catch (refreshError: any) {
      toast.error(
        refreshError?.message || t("catalogTranslationSettings.toasts.healthRefreshError")
      )
    }
  }

  const handleProviderChange = (value: string) => {
    if (!settings) {
      return
    }

    const nextProvider = value as CatalogTranslationProvider
    if (nextProvider === settings.active_provider) {
      return
    }

    const confirmed = confirmDestructiveAction(
      t("catalogTranslationSettings.confirm.providerSwitchDestructive")
    )

    if (!confirmed) {
      return
    }

    setSettings({
      ...settings,
      active_provider: nextProvider,
    })
    setCredentials({})
    setDirtyCredentialKeys([])
    setClearedCredentialKeys([])
    setTestConnectionError(null)
  }

  const focusProviderSection = () => {
    providerSectionRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    })
  }

  const focusCredentialField = (field: string): boolean => {
    const target = credentialInputRefs.current[field]
    if (!target) {
      return false
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })
    target.focus()
    return true
  }

  const focusProviderRecoveryTarget = () => {
    const unresolvedCredentialField = resolveFirstUnresolvedCredentialField({
      settings,
      requiredFields,
      credentials,
      clearedCredentialKeys,
    })

    if (unresolvedCredentialField && focusCredentialField(unresolvedCredentialField)) {
      return
    }

    focusProviderSection()
  }

  const focusFieldScopesSection = () => {
    fieldScopesSectionRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    })
  }

  if (error && !settings) {
    return (
      <SingleColumnPage widgets={{ before: [], after: [] }}>
        <Container className="space-y-4 p-8">
          <Heading level="h1">{t("catalogTranslationSettings.domain")}</Heading>
          <Text>{t("catalogTranslationSettings.errors.initialLoad")}</Text>
          <div>
            <Button variant="secondary" onClick={() => refetch()}>
              {t("catalogTranslationSettings.actions.retry")}
            </Button>
          </div>
        </Container>
      </SingleColumnPage>
    )
  }

  if (isLoading || !settings) {
    return (
      <SingleColumnPage widgets={{ before: [], after: [] }}>
        <Container className="p-8">
          <Text className="text-ui-fg-subtle">
            {t("catalogTranslationSettings.loading")}
          </Text>
        </Container>
      </SingleColumnPage>
    )
  }

  return (
    <>
      <Helmet>
        <title>{t("catalogTranslationSettings.domain")}</title>
      </Helmet>
      <SingleColumnPage widgets={{ before: [], after: [] }}>
        <div className="flex flex-col gap-4">
          {alerts.map((alert) => (
            <Container
              key={alert.code}
              className={clx("px-6 py-4", {
                "border border-ui-border-error bg-ui-bg-base":
                  alert.code === "page_error",
                "border border-ui-border-base":
                  alert.code !== "page_error",
              })}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Text weight="plus">
                    {alert.code === "page_error"
                      ? t("catalogTranslationSettings.errors.saveFailedTitle")
                      : alert.code === "provider_none_or_credentials_not_ready"
                        ? t(
                            "catalogTranslationSettings.warnings.providerNoneOrCredentialsNotReady.title"
                          )
                        : t("catalogTranslationSettings.warnings.allScopesOff.title")}
                  </Text>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {alert.code === "page_error"
                      ? pageError
                      : alert.code === "provider_none_or_credentials_not_ready"
                        ? t(
                            "catalogTranslationSettings.warnings.providerNoneOrCredentialsNotReady.body"
                          )
                        : t("catalogTranslationSettings.warnings.allScopesOff.body")}
                  </Text>
                </div>
                {alert.primaryCta === "retry" ? (
                  <Button variant="secondary" onClick={handleSave}>
                    {t("catalogTranslationSettings.actions.retry")}
                  </Button>
                ) : null}
                {alert.primaryCta === "provider" ? (
                  <Button variant="secondary" onClick={focusProviderRecoveryTarget}>
                    {t("catalogTranslationSettings.actions.reviewProvider")}
                  </Button>
                ) : null}
                {alert.primaryCta === "scope" ? (
                  <Button variant="secondary" onClick={focusFieldScopesSection}>
                    {t("catalogTranslationSettings.actions.reviewScopes")}
                  </Button>
                ) : null}
              </div>
            </Container>
          ))}
          <Container className="flex items-start justify-between gap-4 p-6">
            <div>
            <Heading level="h1">{t("catalogTranslationSettings.domain")}</Heading>
            <Text size="small" className="text-ui-fg-subtle mt-1">
              {t("catalogTranslationSettings.subtitle")}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleTestConnection}
              isLoading={testMutation.isPending}
            >
              {t("catalogTranslationSettings.actions.testConnection")}
            </Button>
            <Button onClick={handleSave} isLoading={updateMutation.isPending}>
              {t("catalogTranslationSettings.actions.saveSettings")}
            </Button>
          </div>
        </Container>

        <Container className="p-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <section className="space-y-4" ref={providerSectionRef}>
                <div>
                  <Heading level="h2">
                    {t("catalogTranslationSettings.provider.header")}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {t("catalogTranslationSettings.provider.hint")}
                  </Text>
                </div>
                <RadioGroup
                  value={settings.active_provider}
                  onValueChange={handleProviderChange}
                >
                  <div className="flex flex-col gap-3">
                    {PROVIDER_OPTIONS.map((provider) => {
                      const isSelected = settings.active_provider === provider

                      return (
                        <label
                          key={provider}
                          htmlFor={`provider-${provider}`}
                          className={getOptionCardClassName(isSelected)}
                        >
                          <RadioGroup.Item value={provider} id={`provider-${provider}`} />
                          <Text size="small" weight="plus" className="capitalize">
                            {t(`catalogTranslationSettings.provider.options.${provider}`)}
                          </Text>
                        </label>
                      )
                    })}
                  </div>
                </RadioGroup>
                {testConnectionSectionError ? (
                  <div className="rounded-lg border border-ui-border-error bg-ui-bg-base px-4 py-3">
                    <Text size="small" weight="plus">
                      {t(testConnectionSectionError.titleKey)}
                    </Text>
                    <Text size="small" className="text-ui-fg-subtle mt-1">
                      {testConnectionSectionError.message}
                    </Text>
                    <div className="mt-3">
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={handleTestConnection}
                        isLoading={testMutation.isPending}
                      >
                        {t("catalogTranslationSettings.actions.testConnection")}
                      </Button>
                    </div>
                  </div>
                ) : null}
                {settings.active_provider !== "none" && (
                  <div className="space-y-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.provider.credentialsMasked")}
                    </Text>
                    <div className="grid gap-4 md:grid-cols-2">
                      {requiredFields.map((field) => (
                        <div key={field} className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <Label htmlFor={field}>
                              {t(`catalogTranslationSettings.provider.credentials.${field}`)}
                            </Label>
                            <div className="flex items-center gap-2">
                              {hasStoredCredential(settings, field) &&
                              !clearedCredentialKeys.includes(field) ? (
                                <Badge size="2xsmall">
                                  {t("catalogTranslationSettings.provider.storedSecret")}
                                </Badge>
                              ) : null}
                              <Button
                                variant="secondary"
                                size="small"
                                type="button"
                                onClick={() => {
                                  const confirmed = confirmDestructiveAction(
                                    t(
                                      "catalogTranslationSettings.confirm.credentialClearDestructive"
                                    )
                                  )

                                  if (!confirmed) {
                                    return
                                  }

                                  setCredentials((current) => ({
                                    ...current,
                                    [field]: "",
                                  }))
                                  setDirtyCredentialKeys((current) =>
                                    current.includes(field) ? current : [...current, field]
                                  )
                                  setClearedCredentialKeys((current) =>
                                    current.includes(field) ? current : [...current, field]
                                  )
                                }}
                              >
                                {t("catalogTranslationSettings.provider.clearCredential")}
                              </Button>
                            </div>
                          </div>
                          <Input
                            id={field}
                            type="password"
                            autoComplete="off"
                            ref={(node) => {
                              credentialInputRefs.current[field] = node
                            }}
                            value={credentials[field] || ""}
                            onChange={(event) => {
                              setCredentials((current) => ({
                                ...current,
                                [field]: event.target.value,
                              }))
                              setClearedCredentialKeys((current) =>
                                current.filter((entry) => entry !== field)
                              )
                              setDirtyCredentialKeys((current) =>
                                current.includes(field) ? current : [...current, field]
                              )
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="space-y-4" ref={fieldScopesSectionRef}>
                <div>
                  <Heading level="h2">
                    {t("catalogTranslationSettings.locales.header")}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {t("catalogTranslationSettings.locales.hint")}
                  </Text>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {localeOptions.map((locale) => {
                    const isChecked = settings.enabled_locales.includes(locale)
                    const isDisabled =
                      settings.enabled_locales.length === 1 && isChecked

                    return (
                      <label
                        key={locale}
                        className={getOptionCardClassName(isChecked, isDisabled)}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) => toggleLocale(locale, checked === true)}
                          disabled={isDisabled}
                        />
                        <Text size="small" weight="plus">
                          {getAdminLanguageDisplayName(locale)}
                        </Text>
                      </label>
                    )
                  })}
                </div>
              </section>

              <section className="space-y-4">
                <div>
                  <Heading level="h2">
                    {t("catalogTranslationSettings.automation.header")}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {t("catalogTranslationSettings.automation.hint")}
                  </Text>
                </div>
                <div className="space-y-3">
                  {AUTOMATION_OPTIONS.map(([key, labelKey]) => {
                    const isChecked = Boolean(settings[key])

                    return (
                      <label key={String(key)} className={getOptionCardClassName(isChecked)}>
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            setSettings({
                              ...settings,
                              [key]: checked === true,
                            })
                          }
                        />
                        <Text size="small" weight="plus">
                          {t(`catalogTranslationSettings.automation.${labelKey}`)}
                        </Text>
                      </label>
                    )
                  })}
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section className="space-y-4">
                <div>
                  <Heading level="h2">
                    {t("catalogTranslationSettings.fieldScopes.header")}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {t("catalogTranslationSettings.fieldScopes.hint")}
                  </Text>
                </div>
                <div className="space-y-3">
                  {FIELD_SCOPE_OPTIONS.map(([key, labelKey]) => {
                    const isChecked = settings.field_scopes?.[key] ?? false

                    return (
                      <label key={String(key)} className={getOptionCardClassName(isChecked)}>
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            updateFieldScope(
                              key,
                              checked === true
                            )
                          }
                        />
                        <Text size="small" weight="plus">
                          {t(`catalogTranslationSettings.fieldScopes.${labelKey}`)}
                        </Text>
                      </label>
                    )
                  })}
                </div>
              </section>

              <section className="space-y-4">
                <div>
                  <Heading level="h2">
                    {t("catalogTranslationSettings.retryPolicy.header")}
                  </Heading>
                  <Text size="small" className="text-ui-fg-subtle mt-1">
                    {t("catalogTranslationSettings.retryPolicy.hint")}
                  </Text>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="max-attempts">
                      {t("catalogTranslationSettings.retryPolicy.maxAttempts")}
                    </Label>
                    <Input
                      id="max-attempts"
                      type="number"
                      min={1}
                      max={10}
                      value={settings.retry_policy.max_attempts}
                      onChange={(event) =>
                        setSettings({
                          ...settings,
                          retry_policy: {
                            ...settings.retry_policy,
                            max_attempts: Number(event.target.value || 1),
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("catalogTranslationSettings.retryPolicy.summary")}</Label>
                    <div className="rounded-lg border border-ui-border-base px-4 py-3 text-sm text-ui-fg-subtle">
                      {t("catalogTranslationSettings.retryPolicy.retryable")}: {" "}
                      {settings.retry_policy.retryable_errors.join(", ")}
                      <br />
                      {t("catalogTranslationSettings.retryPolicy.nonRetryable")}: {" "}
                      {settings.retry_policy.non_retryable_errors.join(", ")}
                    </div>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Heading level="h2">
                      {t("catalogTranslationSettings.providerHealth.header")}
                    </Heading>
                    <Text size="small" className="text-ui-fg-subtle mt-1">
                      {t("catalogTranslationSettings.providerHealth.hint")}
                    </Text>
                  </div>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleRefreshHealth}
                    isLoading={refreshHealthMutation.isPending}
                  >
                    {t("catalogTranslationSettings.actions.refreshHealth")}
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <StatusBadge color={healthColor}>
                    {providerHealthStatus === "healthy"
                      ? t("catalogTranslationSettings.providerHealth.healthy")
                      : providerHealthStatus === "attention"
                        ? t("catalogTranslationSettings.providerHealth.attention")
                        : t("catalogTranslationSettings.providerHealth.idle")}
                  </StatusBadge>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.lastChecked")}
                    </Text>
                    <Text size="small">
                      {settings.provider_health_summary.last_checked_at || "-"}
                    </Text>
                  </div>
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.lastSuccess")}
                    </Text>
                    <Text size="small">
                      {settings.provider_health_summary.last_success_at || "-"}
                    </Text>
                  </div>
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.lastFailure")}
                    </Text>
                    <Text size="small">
                      {settings.provider_health_summary.last_failure_at || "-"}
                    </Text>
                  </div>
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.latestErrorCode")}
                    </Text>
                    <div className="mt-1">
                      {settings.provider_health_summary.last_error_code ? (
                        <Badge color="red">{settings.provider_health_summary.last_error_code}</Badge>
                      ) : (
                        <Text size="small">-</Text>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.snapshotSource")}
                    </Text>
                    <Text size="small">
                      {settings.provider_health_summary.snapshot_source || "-"}
                    </Text>
                  </div>
                  <div className="rounded-lg border border-ui-border-base px-4 py-3">
                    <Text size="small" className="text-ui-fg-subtle">
                      {t("catalogTranslationSettings.providerHealth.nextRetryAfter")}
                    </Text>
                    <Text size="small">
                      {settings.provider_health_summary.next_retry_after || "-"}
                    </Text>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </Container>
        </div>
      </SingleColumnPage>
    </>
  )
}
